import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Collapse,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Paper,
  Divider,
} from "@mui/material";

// MUI v7: Grid (formerly Grid2) is now the default
import Grid from "@mui/material/Grid";

import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
} from "@mui/icons-material";

import { AuthContext } from "../../../context/AuthContext/AuthContext";
import {
  createLabPhlebotomy,
  updateLabPhlebotomy,
  getLabPhlebotomyById,
  getPatientsWithActiveReferrals,
} from "../../specialist-referrals/services/labPhlebotomyService";
import { getStatuses, getLocations } from "../../../utils/services/control/masterDataService";
import { debounce } from "../../specialist-referrals/utils/debounce";

const getInitialFormState = () => ({
  specialistReferralId: null,
  cipId: "",
  patientName: "",
  dob: "",
  memberPhoneNumber: "",

  pcpId: "",
  pcpName: "",

  dateOfService: new Date().toISOString().split("T")[0],
  appointmentTypeId: "",
  locationId: "",
  medicalCenter: "",

  problemsDrawingBlood: false,
  patientReferredOut: null,
  labOrderGiven: null,
  comments: "",
});

const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("T")) return dateStr.split("T")[0];
  if (dateStr.includes("-") && dateStr.length === 10) return dateStr;
  const parts = String(dateStr).split("/");
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return dateStr;
};

const AUTOCOMPLETE_POPPER_SX = {
  "& .MuiPaper-root": { minWidth: 520 },
  "& .MuiAutocomplete-listbox": { maxHeight: 320 },
};

export default function LabPhlebotomyCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { recordId } = useParams();
  const isEditing = Boolean(recordId);

  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(getInitialFormState());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Catalogs from API
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  // Active referrals patient search
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [expanded, setExpanded] = useState({
    patient: true,
    service: true,
    lab: true,
  });

  // Debounced patient search ref
  const patientSearchRef = useRef(null);

  useEffect(() => {
    patientSearchRef.current = debounce(async (term) => {
      if (!term || term.length < 2) { setPatientOptions([]); return; }
      setPatientLoading(true);
      try {
        const results = await getPatientsWithActiveReferrals(term, form.locationId || undefined);
        setPatientOptions(results || []);
      } catch (e) { console.error(e); }
      finally { setPatientLoading(false); }
    }, 300);
  }, [form.locationId]);

  // Load catalogs on mount
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [apptTypes, locs] = await Promise.all([
          getStatuses("lab_phlebotomy_appointment_type"),
          getLocations({ clinical: true, status: 1 }),
        ]);
        setAppointmentTypes(apptTypes || []);
        setLocationOptions(locs || []);

        // Set default appointment type
        if (apptTypes?.length > 0 && !isEditing) {
          setForm((prev) => ({ ...prev, appointmentTypeId: prev.appointmentTypeId || apptTypes[0].statusId }));
        }
      } catch (err) {
        console.error("Error loading catalogs:", err);
      }
    };
    loadCatalogs();
  }, [isEditing]);

  // Pre-fill from referral navigation state (create mode only)
  const prefilledFromReferral = Boolean(location.state?.referral && !isEditing);

  useEffect(() => {
    if (!location.state?.referral || isEditing) return;
    const ref = location.state.referral;
    setForm((prev) => ({
      ...prev,
      specialistReferralId: ref.specialistReferralId,
      cipId: ref.cipId,
      patientName: ref.patientName,
      dob: ref.patientDob ? ref.patientDob.split("T")[0] : "",
      memberPhoneNumber: ref.patientPhone || "",
      pcpId: ref.pcpId || "",
      pcpName: ref.pcpName || "",
      locationId: ref.locationId || "",
      medicalCenter: ref.medicalCenter || "",
    }));
    setSelectedPatient({
      specialistReferralId: ref.specialistReferralId,
      cipId: ref.cipId,
      patientName: ref.patientName,
      patientDob: ref.patientDob,
      patientPhone: ref.patientPhone,
      patientGender: ref.patientGender,
      patientAge: ref.patientAge,
      specialistName: ref.specialistName,
      specialistTypeName: ref.specialistTypeName,
      pcpName: ref.pcpName,
      medicalCenter: ref.medicalCenter,
    });
    // Clear navigation state to avoid re-triggering
    window.history.replaceState({}, document.title);
  }, [location.state, isEditing]);

  // Load existing record for edit mode
  useEffect(() => {
    if (!isEditing || !recordId) return;

    const loadRecord = async () => {
      setLoading(true);
      try {
        const record = await getLabPhlebotomyById(Number(recordId));
        setForm({
          specialistReferralId: record.specialistReferralId,
          cipId: record.cipId || "",
          patientName: record.patientName || "",
          dob: record.patientDob ? record.patientDob.split("T")[0] : "",
          memberPhoneNumber: record.patientPhone || "",
          pcpId: record.pcpId || "",
          pcpName: record.pcpName || "",
          dateOfService: record.dateOfService ? record.dateOfService.split("T")[0] : "",
          appointmentTypeId: record.appointmentTypeId || "",
          locationId: record.locationId || "",
          medicalCenter: record.medicalCenter || "",
          problemsDrawingBlood: record.problemsDrawingBlood ?? false,
          patientReferredOut: record.patientReferredOut ?? null,
          labOrderGiven: record.labOrderGiven ?? null,
          comments: record.comments || "",
        });
        setSelectedPatient({
          specialistReferralId: record.specialistReferralId,
          cipId: record.cipId,
          patientName: record.patientName,
        });
      } catch (err) {
        console.error("Error loading record:", err);
        navigate("/LabPhlebotomy", { state: { message: "Record not found", severity: "error" } });
      } finally {
        setLoading(false);
      }
    };
    loadRecord();
  }, [isEditing, recordId, navigate]);

  const clearFieldError = useCallback((field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  }, []);

  const handleChange = useCallback(
    (field, value) => {
      setForm((prev) => {
        const next = { ...prev, [field]: value };

        if (field === "problemsDrawingBlood" && value === false) {
          next.patientReferredOut = null;
          next.labOrderGiven = null;
          next.comments = "";
        }
        if (field === "patientReferredOut" && value === false) {
          next.labOrderGiven = null;
        }
        if (field === "locationId") {
          const loc = locationOptions?.find((l) => l.locationId === value);
          next.medicalCenter = loc?.medicalCenter || "";
        }

        return next;
      });

      clearFieldError(field);
    },
    [clearFieldError, locationOptions]
  );

  const handleToggle = (field, value) => {
    if (value !== null) handleChange(field, value);
  };

  const handleAccordionChange = (panel) => (_event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // Handle patient selection from active referrals autocomplete
  const handlePatientSelect = (_event, patient) => {
    setSelectedPatient(patient);
    if (!patient) {
      setForm((prev) => ({
        ...prev,
        specialistReferralId: null,
        cipId: "",
        patientName: "",
        dob: "",
        memberPhoneNumber: "",
        pcpId: "",
        pcpName: "",
        locationId: "",
        medicalCenter: "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      specialistReferralId: patient.specialistReferralId,
      cipId: patient.cipId || "",
      patientName: patient.patientName || "",
      dob: patient.patientDob ? patient.patientDob.split("T")[0] : "",
      memberPhoneNumber: patient.patientPhone || "",
      pcpId: patient.pcpId || "",
      pcpName: patient.pcpName || "",
      locationId: patient.locationId || "",
      medicalCenter: patient.medicalCenter || "",
    }));
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!form.cipId?.trim()) newErrors.cipId = "Patient is required";
    if (!form.specialistReferralId) newErrors.cipId = "Patient with active referral is required";
    if (!form.pcpId) newErrors.pcpName = "PCP is required";
    if (!form.dateOfService) newErrors.dateOfService = "Date of service is required";
    if (!form.appointmentTypeId) newErrors.appointmentTypeId = "Appointment type is required";
    if (!form.locationId) newErrors.locationId = "Medical center is required";

    if (form.dateOfService) {
      const serviceDate = new Date(form.dateOfService);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (serviceDate > today) newErrors.dateOfService = "Date cannot be in the future";
    }

    setErrors(newErrors);
    return { ok: Object.keys(newErrors).length === 0, newErrors };
  }, [form]);

  const expandSectionsWithErrors = (newErrors) => {
    const sectionsWithErrors = {
      patient: ["cipId"].some((f) => !!newErrors[f]),
      service: ["dateOfService", "appointmentTypeId", "locationId", "pcpName"].some((f) => !!newErrors[f]),
      lab: false,
    };

    setExpanded((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(sectionsWithErrors)
          .filter(([, v]) => v)
          .map(([k]) => [k, true])
      ),
    }));
  };

  const handleSave = async () => {
    const { ok, newErrors } = validateForm();
    if (!ok) {
      expandSectionsWithErrors(newErrors);
      openSnackbar("Please fix the required fields.", "error");
      return;
    }

    setLoading(true);
    try {
      const employeeId = user?.employeeId || user?.employee || null;

      const payload = {
        specialistReferralId: form.specialistReferralId,
        cipId: form.cipId,
        dateOfService: form.dateOfService,
        appointmentTypeId: Number(form.appointmentTypeId),
        pcpId: Number(form.pcpId),
        locationId: Number(form.locationId),
        employeeStaffId: Number(employeeId),
        problemsDrawingBlood: form.problemsDrawingBlood,
        patientReferredOut: form.patientReferredOut ?? false,
        labOrderGiven: form.labOrderGiven ?? false,
        comments: form.comments || "",
      };

      if (isEditing) {
        payload.labPhlebotomyId = Number(recordId);
        await updateLabPhlebotomy(payload);
      } else {
        await createLabPhlebotomy(payload);
      }

      navigate("/LabPhlebotomy", {
        state: {
          refresh: true,
          message: isEditing ? "Record updated successfully" : "Record created successfully",
          severity: "success",
        },
      });
    } catch (err) {
      console.error("Error saving record:", err);
      const errorMsg = err?.response?.data?.message || "Error saving record";
      openSnackbar(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/LabPhlebotomy");

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto", width: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={handleCancel} size="large">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {isEditing ? "Edit Lab Record" : "New Lab Phlebotomy Record"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditing ? "Update the lab phlebotomy record" : "Create a new lab phlebotomy record"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={handleCancel} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={loading}
            size="large"
          >
            {isEditing ? "Update" : "Save"} Record
          </Button>
        </Stack>
      </Box>

      {/* Grid2: size en lugar de item/xs/md */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          {/* Patient */}
          <Accordion expanded={expanded.patient} onChange={handleAccordionChange("patient")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Patient Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Box sx={{ width: "100%" }}>
                    <Autocomplete
                      options={patientOptions}
                      getOptionLabel={(option) =>
                        option?.patientName
                          ? `${option.patientName} (${option.cipId}) - ${option.specialistTypeName || "Referral"}`
                          : ""
                      }
                      value={selectedPatient}
                      onChange={handlePatientSelect}
                      onInputChange={(_e, value) => {
                        patientSearchRef.current?.(value);
                      }}
                      loading={patientLoading}
                      isOptionEqualToValue={(option, value) =>
                        option.specialistReferralId === value?.specialistReferralId
                      }
                      disabled={isEditing || prefilledFromReferral}
                      noOptionsText="Type to search patients with active referrals..."
                      slotProps={{ popper: { sx: AUTOCOMPLETE_POPPER_SX } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Patient (with active referral) *"
                          placeholder="Type patient name or CIP ID..."
                          error={!!errors.cipId}
                          helperText={errors.cipId || "Only patients with active specialist referrals are shown"}
                          fullWidth
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.specialistReferralId}>
                          <Box sx={{ py: 0.5 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {option.patientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.cipId} | {option.specialistTypeName} | {option.medicalCenter}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Specialist: {option.specialistName} | PCP: {option.pcpName}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Box>

                  {/* Patient Info Card */}
                  {selectedPatient && (
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 2,
                        p: 2.5,
                        bgcolor: "success.50",
                        border: 2,
                        borderColor: "success.main",
                        borderRadius: 2,
                      }}
                    >
                      {/* Header: Name + Status */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <PersonIcon sx={{ fontSize: 32 }} color="success" />
                          <Box>
                            <Typography variant="h6" fontWeight={700}>
                              {selectedPatient.patientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {selectedPatient.patientGender === "M" ? "Male" : selectedPatient.patientGender === "F" ? "Female" : selectedPatient.patientGender || ""}
                              {selectedPatient.patientAge ? ` | ${selectedPatient.patientAge} years old` : ""}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Active Referral"
                          color="success"
                          sx={{ fontWeight: 600, px: 1 }}
                        />
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      {/* Info Grid */}
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            CPID
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {selectedPatient.cipId}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Specialist
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedPatient.specialistName || "N/A"}
                          </Typography>
                        </Grid>
                        {form.dob && (
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <CakeIcon fontSize="small" color="action" />
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Date of Birth
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {form.dob}
                                </Typography>
                              </Box>
                            </Stack>
                          </Grid>
                        )}
                        {form.memberPhoneNumber && (
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <PhoneIcon fontSize="small" color="action" />
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Phone
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                  {form.memberPhoneNumber}
                                </Typography>
                              </Box>
                            </Stack>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Service */}
          <Accordion expanded={expanded.service} onChange={handleAccordionChange("service")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Service Information
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="PCP Name *"
                    value={form.pcpName || ""}
                    error={!!errors.pcpName}
                    helperText={errors.pcpName || "Auto-filled from patient selection"}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: !!form.pcpName }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Service *"
                    value={formatDateForInput(form.dateOfService)}
                    onChange={(e) => handleChange("dateOfService", e.target.value)}
                    required
                    error={!!errors.dateOfService}
                    helperText={errors.dateOfService}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Appointment Type *"
                    value={form.appointmentTypeId}
                    onChange={(e) => handleChange("appointmentTypeId", e.target.value)}
                    error={!!errors.appointmentTypeId}
                    helperText={errors.appointmentTypeId}
                  >
                    <MenuItem value="">Select Type...</MenuItem>
                    {appointmentTypes.map((opt) => (
                      <MenuItem key={opt.statusId} value={opt.statusId}>
                        {opt.statusName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                  <TextField
                    fullWidth
                    select
                    label="Medical Center *"
                    value={form.locationId}
                    onChange={(e) => handleChange("locationId", e.target.value)}
                    required
                    error={!!errors.locationId}
                    helperText={errors.locationId}
                  >
                    <MenuItem value="">Select Center...</MenuItem>
                    {locationOptions.map((loc) => (
                      <MenuItem key={loc.locationId} value={loc.locationId}>
                        {loc.medicalCenter}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          {/* Lab */}
          <Accordion expanded={expanded.lab} onChange={handleAccordionChange("lab")} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ScienceIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  Lab Questions
                </Typography>
                {form.problemsDrawingBlood && (
                  <Chip label="Issues Reported" color="warning" size="small" sx={{ ml: 1 }} />
                )}
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Did you have any problems drawing blood? *
                  </Typography>

                  <ToggleButtonGroup
                    value={form.problemsDrawingBlood}
                    exclusive
                    onChange={(_, v) => handleToggle("problemsDrawingBlood", v)}
                    fullWidth
                  >
                    <ToggleButton value={false} color="success">
                      No
                    </ToggleButton>
                    <ToggleButton value={true} color="error">
                      Yes
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Collapse in={form.problemsDrawingBlood === true}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Please provide additional details about the issues encountered.
                    </Alert>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Comments"
                          value={form.comments}
                          onChange={(e) => handleChange("comments", e.target.value)}
                          multiline
                          rows={3}
                          placeholder="Describe the issues encountered..."
                          inputProps={{ maxLength: 500 }}
                          helperText={`${form.comments.length}/500 characters`}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Was patient referred out?
                        </Typography>

                        <ToggleButtonGroup
                          value={form.patientReferredOut}
                          exclusive
                          onChange={(_, v) => handleToggle("patientReferredOut", v)}
                          fullWidth
                        >
                          <ToggleButton value={false} color="success">
                            No
                          </ToggleButton>
                          <ToggleButton value={true} color="warning">
                            Yes
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <Collapse in={form.patientReferredOut === true}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Did you give the lab order?
                          </Typography>

                          <ToggleButtonGroup
                            value={form.labOrderGiven}
                            exclusive
                            onChange={(_, v) => handleToggle("labOrderGiven", v)}
                            fullWidth
                          >
                            <ToggleButton value={false} color="error">
                              No
                            </ToggleButton>
                            <ToggleButton value={true} color="success">
                              Yes
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Collapse>
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Staff Info - auto-filled from logged-in user */}
          <Alert severity="info" sx={{ mb: 2 }}>
            Staff: <strong>{user?.name || "N/A"}</strong> (auto-assigned from logged-in user)
          </Alert>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
